/**
 * Renderer-side client for Oracle listener service discovery
 * (`oracle-discover-services` IPC). Shared by the connection form's
 * "Discover" button and the connection-error notice's
 * "Browse services on this listener…" fix, so both surfaces present the
 * exact same outcomes for the exact same listener answers.
 */

export type OracleDiscoveryState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; services: string[] }
  | { status: 'empty' }
  | { status: 'error'; message: string };

/** Oracle's default listener port, used when the form still shows `<auto>`. */
const DEFAULT_ORACLE_PORT = 1521;

export function normalizeOraclePort(port: string | number | undefined): number {
  if (port === undefined || port === '<auto>' || port === '') return DEFAULT_ORACLE_PORT;
  const parsed = typeof port === 'number' ? port : Number(port);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_ORACLE_PORT;
}

/**
 * Probe the listener at host:port and fold the IPC result into a settled
 * discovery state (never 'idle'/'loading' — those are the caller's UI states).
 */
export async function runOracleServiceDiscovery(
  host: string | undefined,
  port: string | number | undefined,
  hint?: string
): Promise<OracleDiscoveryState> {
  const trimmedHost = host?.trim();
  if (!trimmedHost) {
    return { status: 'error', message: 'Enter a server/host first.' };
  }
  const normalizedPort = normalizeOraclePort(port);

  try {
    const result = await window.electronAPI?.oracle?.discoverServices({
      host: trimmedHost,
      port: normalizedPort,
      hint: hint?.trim() || undefined,
    });

    if (!result) {
      return { status: 'error', message: 'Service discovery is unavailable.' };
    }
    if (result.error) {
      return { status: 'error', message: result.error };
    }
    if (result.listenerError) {
      return {
        status: 'error',
        message: `Listener unreachable at ${trimmedHost}:${normalizedPort}. ${result.listenerError}`,
      };
    }
    const services = result.services ?? [];
    if (services.length === 0) {
      return { status: 'empty' };
    }
    return { status: 'ok', services };
  } catch (error) {
    return {
      status: 'error',
      message: error instanceof Error ? error.message : 'Unknown discovery error',
    };
  }
}
